import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Exam, ExamQuestion, ExamAnswerValue, ExamAttemptAnswer, ExamGradingResult } from "@/types/exams";
import { ExamQuestionRenderer } from "@/components/exams/ExamQuestionRenderer";
import { ExamResultsView } from "@/components/exams/ExamResultsView";
import { RoomExamGrading } from "@/components/rooms/RoomExamGrading";
import { ExamChatPanel, QuestionContext } from "@/components/exams/ExamChatPanel";

import { X, Loader2, RotateCcw, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { incrementUsage } from "@/hooks/useUsageLimits";

type AnswerMap = Record<string, ExamAnswerValue>;

const buildAutosaveKey = (examId: string) => `exam-answers-${examId}`;

type PageMode = "taking" | "results";

export const RoomExamPage: React.FC = () => {
  const { roomId, examId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modeFromUrl = searchParams.get("mode");

  const [exam, setExam] = useState<Exam | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showGradingDialog, setShowGradingDialog] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  
  // Results state
  const [pageMode, setPageMode] = useState<PageMode>("taking");
  const [gradingResult, setGradingResult] = useState<ExamGradingResult | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [questionContext, setQuestionContext] = useState<QuestionContext | null>(null);

  // Fetch exam + questions
  useEffect(() => {
    const fetchExam = async () => {
      if (!examId) return;

      setLoading(true);

      // Fetch exam record
      const { data: examRow, error: examError } = await supabase
        .from("exams")
        .select("*")
        .eq("id", examId)
        .maybeSingle();

      if (examError || !examRow) {
        console.error("Error fetching exam:", examError);
        toast.error("Failed to load exam");
        navigate(roomId ? `/room/${roomId}` : "/dashboard");
        return;
      }

      // If exam is still pending or generating, poll for status
      if (examRow.status === "pending" || examRow.status === "generating") {
        // Wait and retry
        setTimeout(fetchExam, 2000);
        return;
      }

      if (examRow.status === "failed") {
        toast.error("Exam generation failed. Please try again.");
        navigate(roomId ? `/room/${roomId}` : "/dashboard");
        return;
      }

      // Fetch questions
      const { data: questionRows, error: questionsError } = await supabase
        .from("exam_questions")
        .select("*")
        .eq("exam_id", examId)
        .order("order_index", { ascending: true });

      if (questionsError) {
        console.error("Error fetching questions:", questionsError);
        toast.error("Failed to load exam questions");
        navigate(roomId ? `/room/${roomId}` : "/dashboard");
        return;
      }

      const questions: ExamQuestion[] = (questionRows || []).map((q: any) => ({
        id: q.id,
        type: q.type,
        content: q.content,
        order_index: q.order_index,
      }));

      const fullExam: Exam = {
        id: examRow.id,
        room_id: examRow.room_id,
        title: examRow.title ?? "Exam",
        status: examRow.status as Exam['status'],
        duration_minutes: examRow.duration_minutes ?? null,
        question_count: questions.length,
        questions,
      };

      setExam(fullExam);

      // Load autosaved answers if any
      const autosaveKey = buildAutosaveKey(examRow.id);
      const saved = localStorage.getItem(autosaveKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as AnswerMap;
          setAnswers(parsed);
        } catch {
          // ignore
        }
      }

      // Setup timer
      if (fullExam.duration_minutes && fullExam.duration_minutes > 0) {
        setRemainingSeconds(fullExam.duration_minutes * 60);
      } else {
        setRemainingSeconds(null);
      }

      // Check if we should show results mode from URL
      if (modeFromUrl === "results") {
        setLoadingResults(true);
        // Fetch the latest completed attempt for this exam
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: latestAttempt } = await supabase
            .from("exam_attempts")
            .select("*")
            .eq("exam_id", examId)
            .eq("user_id", user.id)
            .eq("status", "completed")
            .order("submitted_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (latestAttempt) {
            // Fetch the attempt answers
            const { data: attemptAnswers } = await supabase
              .from("exam_attempt_answers")
              .select("*")
              .eq("attempt_id", latestAttempt.id)
              .order("question_index", { ascending: true });

            if (attemptAnswers) {
              const gradingResultData: ExamGradingResult = {
                attempt_id: latestAttempt.id,
                score: latestAttempt.score ?? 0,
                total_questions: latestAttempt.total_questions,
                total_correct: latestAttempt.total_correct ?? 0,
                results: attemptAnswers.map((a: any) => ({
                  id: a.id,
                  attempt_id: a.attempt_id,
                  question_id: a.question_id,
                  question_index: a.question_index,
                  user_answer: a.user_answer,
                  is_correct: a.is_correct,
                  score: a.score,
                  explanation: a.explanation,
                })),
              };
              setGradingResult(gradingResultData);
              setAttemptId(latestAttempt.id);
              setPageMode("results");
            }
          }
        }
        setLoadingResults(false);
      }

      setLoading(false);
    };

    fetchExam();
  }, [examId, navigate, roomId, modeFromUrl]);

  // Timer countdown
  useEffect(() => {
    if (remainingSeconds === null) return;
    if (remainingSeconds <= 0) {
      handleSubmit(true);
      return;
    }

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSeconds]);

  // Autosave answers to localStorage
  useEffect(() => {
    if (!exam?.id) return;
    const key = buildAutosaveKey(exam.id);
    localStorage.setItem(key, JSON.stringify(answers));
  }, [answers, exam?.id]);

  const questions = exam?.questions ?? [];
  const totalQuestions = questions.length;

  const answeredCount = useMemo(
    () =>
      Object.entries(answers).filter(
        ([, value]) =>
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
      ).length,
    [answers]
  );

  const progressPercent =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const updateAnswer = (questionId: string, value: ExamAnswerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleClose = () => {
    if (roomId) {
      navigate(`/room/${roomId}`);
    } else {
      navigate("/dashboard");
    }
  };

  const handleSubmit = async (fromTimeout = false) => {
    if (!exam || submitting) return;
    setSubmitting(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to submit an exam");
        setSubmitting(false);
        return;
      }

      // 1. Create exam attempt with status 'grading'
      const { data: attempt, error: attemptError } = await supabase
        .from('exam_attempts')
        .insert({
          exam_id: exam.id,
          user_id: user.id,
          total_questions: exam.questions.length,
          status: 'grading',
          duration_seconds: exam.duration_minutes 
            ? (exam.duration_minutes * 60) - (remainingSeconds ?? 0)
            : null,
        })
        .select()
        .single();

      if (attemptError || !attempt) {
        console.error("Failed to create exam attempt:", attemptError);
        toast.error("Failed to submit exam");
        setSubmitting(false);
        return;
      }

      // Track usage for exam attempt
      await incrementUsage("exams");

      // 2. Insert all user answers
      const answersToInsert = exam.questions.map((q, idx) => ({
        attempt_id: attempt.id,
        question_id: q.id,
        question_index: idx,
        user_answer: answers[q.id] ?? null,
      }));

      const { error: answersError } = await supabase
        .from('exam_attempt_answers')
        .insert(answersToInsert);

      if (answersError) {
        console.error("Failed to insert answers:", answersError);
        toast.error("Failed to save answers");
        setSubmitting(false);
        return;
      }

      // 3. Show grading dialog and set attempt ID
      setAttemptId(attempt.id);
      setShowGradingDialog(true);

      // 4. Invoke grading edge function
      const { data: result, error: gradingError } = await supabase.functions.invoke('grade-exam', {
        body: { attempt_id: attempt.id },
      });

      if (gradingError || !result) {
        console.error("Grading failed:", gradingError);
        setShowGradingDialog(false);
        toast.error("Failed to grade exam. Please try again.");
        setSubmitting(false);
        return;
      }

      // 5. Clear autosave
      localStorage.removeItem(buildAutosaveKey(exam.id));

      // 6. Store grading result and switch to results mode
      setGradingResult(result as ExamGradingResult);
      
      // Brief delay for dialog animation
      setTimeout(() => {
        setShowGradingDialog(false);
        setPageMode("results");
        setSubmitting(false);
        
        if (fromTimeout) {
          toast.info("Time's up! See your results below.");
        }
      }, 1000);

    } catch (error) {
      console.error(error);
      setShowGradingDialog(false);
      toast.error("Failed to submit exam");
      setSubmitting(false);
    }
  };

  const handleGradingComplete = (result: { score: number; attemptId: string }) => {
    // This is handled inline in handleSubmit for now
  };

  const handleGradingError = (error: string) => {
    setShowGradingDialog(false);
    toast.error(error);
    setSubmitting(false);
  };

  if (loading || !exam) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            {exam?.status === "generating" ? "Generating exam questions..." : "Loading exam..."}
          </p>
        </div>
      </div>
    );
  }

  // Loading skeleton for results mode
  if (loadingResults || (modeFromUrl === "results" && !gradingResult)) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        {/* Header skeleton */}
        <header className="sticky top-0 z-10 w-full border-b bg-background/95 px-4 py-3">
          <div className="mx-auto flex w-full items-center justify-between">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex flex-1 items-center justify-center gap-2 px-4">
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-3 w-full max-w-2xl" />
              <Skeleton className="h-4 w-8" />
            </div>
            <Skeleton className="h-10 w-28" />
          </div>
        </header>

        {/* Content skeleton */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto mt-8 w-full px-4 pb-24 lg:w-4/5 xl:w-3/5 2xl:w-1/2">
            {/* Score card skeleton */}
            <div className="mb-8 rounded-2xl border p-6">
              <div className="flex flex-col items-center gap-4">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-24 w-24 rounded-full" />
                <Skeleton className="h-6 w-32" />
              </div>
            </div>

            {/* Question skeletons */}
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-5 w-3/4" />
                    </div>
                  </div>
                  <div className="space-y-3 pl-11">
                    <Skeleton className="h-12 w-full rounded-lg" />
                    <Skeleton className="h-12 w-full rounded-lg" />
                    <Skeleton className="h-12 w-full rounded-lg" />
                    <Skeleton className="h-12 w-full rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const timerDisplay = formatTime(remainingSeconds);
  const isLowTime = remainingSeconds !== null && remainingSeconds < 60;

  // Results Mode
  if (pageMode === "results" && gradingResult) {
    return (
      <div className="flex min-h-screen bg-background">
        {/* Left side - Results content with header and footer */}
        <div className={`flex min-h-screen flex-1 flex-col transition-all duration-300 ${isChatOpen ? 'hidden lg:flex lg:mr-[400px] xl:mr-[480px] 2xl:mr-[576px]' : 'flex'}`}>
          {/* Top bar - Results */}
          <header className="sticky top-0 z-10 w-full border-b bg-background/95 px-4 shadow-lg shadow-neutral-800/5 backdrop-blur-sm dark:border-border dark:shadow-white/5">
            <div className="relative mx-auto flex w-full items-center">
              {/* Left: close */}
              <div className="flex w-20 flex-shrink-0 items-center gap-2 xl:w-40">
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              {/* Center: Progress indicator */}
              <div className="flex flex-1 items-center justify-center gap-2">
                <span className="text-sm text-muted-foreground">{gradingResult.total_correct}</span>
                <Progress 
                  value={(gradingResult.total_correct / gradingResult.total_questions) * 100} 
                  className="h-3 w-full max-w-2xl bg-primary/5 dark:bg-primary/10" 
                />
                <span className="text-sm text-muted-foreground">{gradingResult.total_questions}</span>
              </div>

              {/* Right: Space Chat button */}
              <div className="flex w-20 flex-shrink-0 items-center justify-end gap-2 xl:w-40">
                <div className="z-10 flex justify-end bg-background p-2 pr-0 md:p-4 md:pr-0">
                  <Button
                    variant="outline"
                    className="size-icon gap-x-2 xl:size-auto xl:px-4"
                    onClick={() => setIsChatOpen(!isChatOpen)}
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span className="hidden xl:inline">{isChatOpen ? 'Close Chat' : 'Room Chat'}</span>
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto mt-8 w-full flex-grow px-4 pb-24 lg:w-4/5 xl:w-3/5 2xl:w-1/2">
              <ExamResultsView
                questions={questions}
                attemptAnswers={gradingResult.results}
                score={gradingResult.score}
                totalCorrect={gradingResult.total_correct}
                totalQuestions={gradingResult.total_questions}
                onAskChat={(questionIndex, questionText) => {
                  setQuestionContext({ questionIndex, questionText });
                  setIsChatOpen(true);
                }}
              />
            </div>
          </main>

          {/* Bottom sticky - Two button footer */}
          <div className="sticky bottom-0 z-10">
            <div className="border-t-[1.5px] border-border bg-background px-4 py-8 md:py-[clamp(1rem,2.5vh,2.5rem)]">
              <div className="mx-auto flex max-w-4xl justify-center gap-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-full border border-input bg-background px-8 text-base font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:w-96"
                >
                  Go to Room
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/room/${roomId}/exam/${examId}/summary`)}
                  className="inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-full bg-primary px-8 text-base font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:w-96"
                >
                  View Summary
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Panel - fixed positioning handled by ExamChatPanel */}
        {isChatOpen && (
          <ExamChatPanel
            onClose={() => {
              setIsChatOpen(false);
              setQuestionContext(null);
            }}
            questionContext={questionContext}
            onClearContext={() => setQuestionContext(null)}
          />
        )}
      </div>
    );
  }

  // Taking Mode
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-10 w-full border-b border-border bg-background/95 px-4 shadow-lg shadow-neutral-800/5 backdrop-blur-sm dark:shadow-white/5 py-4">
        <div className="relative mx-auto flex w-full items-center">
          {/* Left: close */}
          <div className="flex w-20 flex-shrink-0 items-center gap-2 xl:w-40">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Center: progress */}
          <div className="flex flex-1 items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground">
              {answeredCount}
            </span>
            <div className="relative h-3 w-full max-w-2xl overflow-hidden rounded-full bg-primary/5 dark:bg-primary/10">
              <div
                className="h-full flex-1 bg-primary transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {totalQuestions}
            </span>
          </div>

          {/* Right: timer */}
          <div className="flex w-20 flex-shrink-0 items-center justify-end gap-2 xl:w-40">
            {timerDisplay && (
              <span
                className={`flex-shrink-0 text-sm font-medium ${
                  isLowTime ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {timerDisplay}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Exam content */}
      <main className="flex-1">
        <div className="mx-auto mt-16 w-full flex-grow overflow-y-auto px-4 pb-24 lg:w-3/5 2xl:w-1/2">
          <div className="flex flex-col gap-24">
            {questions.map((q, idx) => (
              <ExamQuestionRenderer
                key={q.id}
                question={q}
                index={idx}
                answer={answers[q.id] ?? null}
                onChange={(val) => updateAnswer(q.id, val)}
              />
            ))}
          </div>
        </div>
      </main>

      {/* Bottom sticky submit */}
      <div className="sticky bottom-0 z-10">
        <div className="border-t-[1.5px] border-border bg-background px-4 py-8 md:py-[clamp(1rem,2.5vh,2.5rem)]">
          <div className="mx-auto flex max-w-4xl justify-center gap-4">
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSubmit(false)}
              className="inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-full bg-primary px-8 text-base font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:w-96"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Exam"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Grading dialog */}
      <RoomExamGrading
        isOpen={showGradingDialog}
        attemptId={attemptId}
        onComplete={handleGradingComplete}
        onError={handleGradingError}
      />
    </div>
  );
};
